import type { PortalFollowUpDto } from '@signalvote/shared';
import { skip, type CurrentRead } from '@signalvote/data';
import { apiJson } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';

export type { PortalFollowUpDto };

const portalFollowUps = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'portal-follow-ups', reportId],
  reads: 'GET /api/finance/reports/{id}/portal-follow-ups',
  fetch: (reportId, { signal, campaignId }) =>
    apiJson<PortalFollowUpDto[]>(`/api/finance/reports/${reportId}/portal-follow-ups`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch portal follow-ups',
    }),
});

/**
 * GitHub #34 — the per-report "Portal follow-ups" checklist. Portal-handoff obligations the
 * electronic filing itself can't satisfy (an AL In-Kind "Other" row keyed by hand in the FCPA
 * portal, a refund's record-then-amend step, a Major Contribution Report, per-loan guarantors,
 * the portal-only AL amendment) are snapshotted at file time so a treasurer returning later
 * still sees what they owe.
 */
export function usePortalFollowUps(reportId: string | undefined): CurrentRead<PortalFollowUpDto[]> {
  return portalFollowUps(reportId ?? skip);
}

type FollowUpInput = { reportId: string; followUpId: string };

/** Marks a portal follow-up done (treasurer handled it in the portal); the write set refreshes the checklist. */
const useCompletePortalFollowUpWrite = defineWrite({
  send: ({ reportId, followUpId }: FollowUpInput) =>
    apiJson<PortalFollowUpDto>(`/api/finance/reports/${reportId}/portal-follow-ups/${followUpId}/complete`, {
      method: 'POST',
      error: 'Failed to mark the follow-up done',
    }),
});

export function useCompletePortalFollowUp() {
  const { mutate, mutateAsync, isPending } = useCompletePortalFollowUpWrite();
  return { completeFollowUp: mutate, completeFollowUpAsync: mutateAsync, isCompleting: isPending };
}

/** Reopens a completed portal follow-up (back to Open); the write set refreshes the checklist. */
const useReopenPortalFollowUpWrite = defineWrite({
  send: ({ reportId, followUpId }: FollowUpInput) =>
    apiJson<PortalFollowUpDto>(`/api/finance/reports/${reportId}/portal-follow-ups/${followUpId}/reopen`, {
      method: 'POST',
      error: 'Failed to reopen the follow-up',
    }),
});

export function useReopenPortalFollowUp() {
  const { mutate, mutateAsync, isPending } = useReopenPortalFollowUpWrite();
  return { reopenFollowUp: mutate, reopenFollowUpAsync: mutateAsync, isReopening: isPending };
}
